import { FieldPath } from 'react-hook-form';

import dayjs from 'dayjs';

import { RHFTextInput } from 'components/input/RHFTextInput';
import { SubmitCheckCallProps } from 'lib/api/submitCheckCall';
import { EmailMode } from 'types/EmailMode';
import { Note } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import CheckCallSource from 'types/enums/CheckCallSource';
import { TMS } from 'types/enums/Integrations';

// ============================================================================
// Enums
// ============================================================================

export enum TimeUnits {
  Minutes = 'Minutes',
  Hours = 'Hours',
  Days = 'Days',
}

export enum CheckInName {
  Dispatch = 'dispatch',
  Pickup = 'pickup',
  AfterPickup = 'afterPickup',
  InTransit = 'inTransit',
  Dropoff = 'dropoff',
  AfterDropoff = 'afterDropoff',
  PodCollection = 'podCollection',
}

// ============================================================================
// Carrier Check-In Types
// ============================================================================

export interface CheckInDetails {
  checkIn: boolean;
  sendTime: Date;
  duration: number;
  timeUnit: TimeUnits;
  emailMode?: EmailMode;
  from?: string; // Email address to send from (from inboxEmailAddress)
  recipients: string[];
  cc: string[];
  subject: string;
  emailDraft: string;
  // Threading fields for reply mode
  threadID?: string;
  threadItemID?: string;
  threadReferences?: string;
  inReplyTo?: string[];
  // Field for ReplyToSubject mode - subject of the thread to reply to
  replyToSubject?: string;
}

export type PODCollectionDetails = Omit<
  CheckInDetails,
  'timeUnit' | 'duration' | 'sendTime'
>;

export interface CarrierCheckInInputs {
  freightTrackingId: string;
  dispatch: CheckInDetails;
  pickup: CheckInDetails;
  afterPickup: CheckInDetails;
  inTransit: Omit<CheckInDetails, 'duration' | 'sendTime'> & {
    frequency: number;
  };
  dropoff: CheckInDetails;
  afterDropoff: CheckInDetails;
  podCollection: PODCollectionDetails;
}

type CheckInSummaryData =
  | CheckInDetails
  | (Omit<CheckInDetails, 'duration' | 'sendTime'> & { frequency: number })
  | PODCollectionDetails;

export type CheckInSummaryItem = {
  name: CheckInName;
  label: string;
  data: Maybe<CheckInSummaryData>;
  baseTime: Maybe<dayjs.Dayjs>;
  timezone: Maybe<string>;
  isBefore: boolean;
  defaultDuration?: number;
  isInTransit?: boolean;
};

// ============================================================================
// Shipper Check-In Types
// ============================================================================

export interface ShipperCheckInInputs {
  freightTrackingId: string;
  frequency: string;
  timeUnit: string;
  defaultCarrierCheckInSettings: boolean;
  dropoffAppt: boolean;
  pickupAppt: boolean;
  loaded: boolean;
  defaultFrequency: string;
  unloaded: boolean;
}

// ============================================================================
// Notes Types
// ============================================================================

export type NoteInputs = Omit<Note, 'isException' | 'isOnTime'> & {
  isOnTime: 'Yes' | 'No'; // Primarily for Relay
  isException: 'Yes' | 'No'; // Primarily for Relay
};

// ============================================================================
// Check Calls Types and Constants
// ============================================================================

export interface StatusMapping {
  [key: string]: {
    [key in TMS]: Maybe<string | { code: number; value: string }>;
  };
}

export const statusMapping: StatusMapping = {
  'at pickup': {
    aljex: 'At Shipper',
    turvo: { code: 2104, value: 'At pickup' },
    relay: 'Mark Arrived at Pickup',
    tai: null,
    mcleodenterprise: null, // TODO
    mcleod: null, // TODO
    globaltranztms: null, // TODO
    threeg: null, // TODO
    freightflow: null, // TODO
    revenova: null, // TODO
    quantumedge: null, // TODO
    stark: null, // TODO
    transportpro: null, // TODO
    [TMS.MercurygateBoomi]: null, // TODO
    [TMS.PLS]: null, // TODO
  },
  loaded: {
    aljex: 'Loaded',
    turvo: { code: 2115, value: 'Picked up' },
    relay: 'Mark Loaded',
    tai: null,
    mcleodenterprise: null, // TODO
    mcleod: null, // TODO
    globaltranztms: null, // TODO
    threeg: null, // TODO
    freightflow: null, // TODO
    revenova: null, // TODO
    quantumedge: null, // TODO
    stark: null, // TODO
    transportpro: null, // TODO
    [TMS.MercurygateBoomi]: null, // TODO
    [TMS.PLS]: null, // TODO
  },
  'in-transit': {
    aljex: 'In Transit',
    turvo: { code: 2105, value: 'En route' },
    relay: 'In Transit Update',
    tai: null,
    mcleodenterprise: null, // TODO
    mcleod: null, // TODO
    globaltranztms: null, // TODO
    threeg: null, // TODO
    freightflow: null, // TODO
    revenova: null, // TODO
    quantumedge: null, // TODO
    stark: null, // TODO
    transportpro: null, // TODO
    [TMS.MercurygateBoomi]: null, // TODO
    [TMS.PLS]: null, // TODO
  },
  'at dropoff': {
    aljex: 'At Consignee',
    turvo: { code: 2106, value: 'At delivery' },
    relay: 'Mark Arrived at Delivery',
    tai: null,
    mcleodenterprise: null, // TODO
    mcleod: null, // TODO
    globaltranztms: null, // TODO
    threeg: null, // TODO
    freightflow: null, // TODO
    revenova: null, // TODO
    quantumedge: null, // TODO
    stark: null, // TODO
    transportpro: null, // TODO
    [TMS.MercurygateBoomi]: null, // TODO
    [TMS.PLS]: null, // TODO
  },
  unloaded: {
    aljex: 'Delivered',
    turvo: { code: 2107, value: 'Delivered' },
    relay: 'Mark Delivered',
    tai: null,
    mcleodenterprise: null, // TODO
    mcleod: null, // TODO
    globaltranztms: null, // TODO
    threeg: null, // TODO
    freightflow: null, // TODO
    revenova: null, // TODO
    quantumedge: null, // TODO
    stark: null, // TODO
    transportpro: null, // TODO
    [TMS.MercurygateBoomi]: null, // TODO
    [TMS.PLS]: null, // TODO
  },
};

export const aljexStatusToLoadTimestampMapping: { [key: string]: string } = {
  'at shipper': 'Pickup Start Time',
  loaded: 'Pickup End Time',
  'at consignee': 'Delivery Start Time',
  delivered: 'Delivery End Time',
};

export const aljexStatusEnums = [
  // Custom additional codes to support milestone events
  { value: 'At Consignee' },
  { value: 'At Shipper' },

  { value: 'Loaded' },
  { value: 'Delivered' },
  { value: 'Appt Needed/Change' },
  { value: 'Arrive Terminal' },
  { value: 'At Broker' },
  { value: 'Bad Info' },
  { value: 'Cons Closed' },
  { value: 'Custom Released' },
  { value: 'Customs Delay' },
  { value: 'Delay' },
  { value: 'Depart Terminal' },
  { value: 'Detention' },
  { value: 'Dot Shutdown' },
  { value: 'Empty Return' },
  { value: 'Holiday' },
  { value: 'In Transit' },
  { value: 'In-Gate' },
  { value: 'Labor' },
  { value: 'Left Message' },
  { value: 'Loaded Late' },
  { value: 'Margin Alert' },
  { value: 'Mech Breakdown' },
  { value: 'Mechincal Breakdown' },
  { value: 'Notify' },
  { value: 'On Time' },
  { value: 'Os&D' },
  { value: 'Other' },
  { value: 'Out 4 Delivery' },
  { value: 'Out Gate' },
  { value: 'Refused' },
  { value: 'Routing' },
  { value: 'Running Late' },
  { value: 'Storage' },
  { value: 'Traffic' },
  { value: 'Undeliverable' },
  { value: 'Unidentified Freight' },
  { value: 'Weather Delays' },
];

export const aljexReasonEnums = [
  { value: 'Accident' },
  { value: 'Alternate Carrier Delivered' },
  { value: 'Carrier Keying Error' },
  { value: 'Consignee - Related' },
  { value: 'Customer Requires Earlier Del.' },
  { value: 'Customer Requested Future Del' },
  { value: 'Failed To Call For Appt.' },
  { value: 'Hold Due To Customs' },
  { value: 'Held Per Customer' },
  { value: 'Industry Disruption' },
  { value: 'Incorrect Address' },
  { value: 'Mechincal Breakdown' },
  { value: 'Missed Delivery' },
  { value: 'Missed Pickup' },
  { value: 'No Req Arrival Date By Shipper' },
  { value: 'Normal Appt.' },
  { value: 'Normal Status' },
  { value: 'Overnite Related' },
  { value: 'Previous Stop' },
  { value: 'Recipient Unavailable - Delay' },
  { value: 'Refused By Recipient' },
  { value: 'Shipper Related' },
  { value: 'Unable To Locate' },
  { value: 'Weather Related' },
];

export const relayStatusEnums = [
  { value: 'Dispatch Driver' },
  { value: 'Mark Loaded' },
  { value: 'In Transit Update' },
  { value: 'Mark Delivered' },
  { value: 'Add Tracking Note' },
  { value: 'Mark Arrived at Pickup' },
  { value: 'Mark Arrived at Delivery' },
];

export const relayReasonEnums = [
  { value: 'Accident' },
  { value: 'Consignee Related' },
  { value: 'Driver Related' },
  { value: 'Mechanical Breakdown' },
  { value: 'Other Carrier Related' },
  { value: 'Previous Stop' },
  { value: 'Shipper Related' },
  { value: 'Holiday' },
  { value: 'Weather' },
  { value: 'Return To Shipper' },
  { value: 'Driver Not Available' },
  { value: 'Processing Delay' },
  { value: 'Trailer Not Available' },
  { value: 'Insufficient Time to Complete Delivery' },
];

export const turvoStatusList = [
  { code: 2100, value: 'Quote active' },
  { code: 2101, value: 'Tendered' },
  { code: 2102, value: 'Covered' },
  { code: 2103, value: 'Dispatched' },
  { code: 2104, value: 'At pickup' },
  { code: 2115, value: 'Picked up' },
  { code: 2105, value: 'En route' },
  { code: 2106, value: 'At delivery' },
  { code: 2107, value: 'Delivered' },
  { code: 2116, value: 'Route Complete' },
  { code: 2108, value: 'Ready for billing' },
  { code: 2109, value: 'Processing' },
  { code: 2110, value: 'Carrier paid' },
  { code: 2111, value: 'Customer paid' },
  { code: 2112, value: 'Completed' },
  { code: 2113, value: 'Canceled' },
  { code: 2117, value: 'Tender - offered' },
  { code: 2118, value: 'Tender - accepted' },
  { code: 2119, value: 'Tender - rejected' },
  { code: 2114, value: 'Quote inactive' },
];

export const tmsToStatusEnumsMap: { [key in TMS]: { value: string }[] } = {
  [TMS.Aljex]: aljexStatusEnums,
  [TMS.Relay]: relayStatusEnums,
  [TMS.Turvo]: turvoStatusList,
  [TMS.Tai]: [], // TODO
  [TMS.McleodEnterprise]: [], // TODO
  [TMS.Mcleod]: [], // TODO
  [TMS.GlobalTranzTMS]: [], // TODO
  [TMS.ThreeG]: [], // TODO
  [TMS.FreightFlow]: [], // TODO
  [TMS.Revenova]: [], // TODO
  [TMS.QuantumEdge]: [], // TODO
  [TMS.Stark]: [], // TODO
  [TMS.TransportPro]: [], // TODO
  [TMS.MercurygateBoomi]: [], // TODO
  [TMS.PLS]: [], // TODO
};

export const tmsToReasonEnumsMap: { [key in TMS]: { value: string }[] } = {
  [TMS.Aljex]: aljexReasonEnums,
  [TMS.Relay]: relayReasonEnums,
  [TMS.Turvo]: [], // TODO
  [TMS.Tai]: [], // TODO
  [TMS.McleodEnterprise]: [], // TODO
  [TMS.Mcleod]: [], // TODO
  [TMS.GlobalTranzTMS]: [], // TODO
  [TMS.ThreeG]: [], // TODO
  [TMS.FreightFlow]: [], // TODO
  [TMS.Revenova]: [], // TODO
  [TMS.QuantumEdge]: [], // TODO
  [TMS.Stark]: [], // TODO
  [TMS.TransportPro]: [], // TODO
  [TMS.MercurygateBoomi]: [], // TODO
  [TMS.PLS]: [], // TODO
};

export type CheckCallInputs = Omit<
  SubmitCheckCallProps,
  'isOnTime' | 'isException'
> & {
  isOnTime: 'Yes' | 'No';
  isException: 'Yes' | 'No';
};

function aljexIsMilestoneFunc(status: Maybe<string>): boolean {
  if (!status) {
    return false;
  }

  status = status.toLowerCase();

  return (
    status === 'at shipper' ||
    status === 'at consignee' ||
    status === 'loaded' ||
    status === 'delivered'
  );
}

function relayIsMilestoneFunc(status: Maybe<string>): boolean {
  if (!status) {
    return false;
  }

  status = status.toLowerCase();

  return (
    status.includes('mark arrived at') ||
    status.includes('loaded') ||
    status.includes('delivered')
  );
}

/**
 * A mapping of TMS names to their corresponding
 * functions that determine if a status represents a key milestone in the shipment process.
 *
 * The milestones are defined as:
 *  1. At pickup
 *  2. Loaded
 *  3. At dropoff
 *  4. Delivered
 *
 * Each function takes a TMS-specific status enum, which is a `Maybe<string>`, and returns `true` if the status
 * corresponds to one of the milestones, or `false` otherwise.
 *
 * @type {Object.<string, function(Maybe<string>): boolean>}
 */
export const tmsToIsMilestoneMap: {
  [key in TMS]: Maybe<(status: Maybe<string>) => boolean>;
} = {
  [TMS.Aljex]: aljexIsMilestoneFunc,
  [TMS.Relay]: relayIsMilestoneFunc,
  [TMS.Turvo]: null, // TODO
  [TMS.Tai]: null,
  [TMS.McleodEnterprise]: null, // TODO
  [TMS.Mcleod]: null, // TODO
  [TMS.GlobalTranzTMS]: null, // TODO
  [TMS.ThreeG]: null, // TODO
  [TMS.FreightFlow]: null, // TODO
  [TMS.Revenova]: null, // TODO
  [TMS.QuantumEdge]: null, //
  [TMS.Stark]: null, // TODO
  [TMS.TransportPro]: null, // TODO
  [TMS.MercurygateBoomi]: null, // TODO
  [TMS.PLS]: null, // TODO
};

export const relaySourceEnums = [
  { value: CheckCallSource.Dispatcher },
  { value: CheckCallSource.Driver },
  { value: CheckCallSource.Shipper_Receiver },
  { value: CheckCallSource.Web },
];

// ============================================================================
// Exceptions Types and Constants
// ============================================================================

export const aljexExceptionCodeEnums = [
  { value: 'HOLD - HD' },
  { value: 'PUAPPT - PU' },
  { value: 'LATEPU - LP' },
  { value: 'PUNUMB - PU' },
  { value: 'BRKDWNS - BK' },
  { value: 'DELAPPT - DA' },
  { value: 'LATEDEL - LD' },
  { value: 'DETENT - DT' },
  { value: 'DELNUMB - DN' },
  { value: 'TNU - TN' },
  { value: 'NOTREADY - NR' },
  { value: 'DAMAGE - DM' },
  { value: 'CLAIM - CL' },
  { value: 'ACCT - AC' },
  { value: 'LUMPER - LM' },
  { value: 'POD - PD' },
  { value: 'CARRINV - VR' },
  { value: 'CUSTINV - CI' },
  { value: 'OTHER - OT' },
];

export const tmsToExceptionCodeEnumsMap: {
  [key: string]: { value: string }[];
} = {
  [TMS.Aljex]: aljexExceptionCodeEnums,
  [TMS.Relay]: [],
};

export interface ExceptionInputs {
  freightTrackingId: string;
  isOnTime: 'Yes' | 'No'; // primarily for Relay
  source: string; // primarily for Relay
  exceptionCode: string;
  driver: string;
  carrier: string;
  trailer: string;
  note: string;
  datetime: Date;
}

// ============================================================================
// Component Input Props Types
// ============================================================================

export type CarrierCheckInTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & {
  name: FieldPath<CarrierCheckInInputs>;
};

export type ShipperCheckInTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & {
  name: FieldPath<ShipperCheckInInputs>;
};

export type NoteTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & {
  name: FieldPath<NoteInputs>;
};

export type CheckCallTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & {
  name: FieldPath<CheckCallInputs>;
};

export type ExceptionTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & {
  name: FieldPath<ExceptionInputs>;
};
